/** @typedef {'text-to-video'|'image-to-video'|'video-to-video'|'music-to-video'|'motion-sync'} StudioGenerationModeId */

export const STUDIO_GENERATION_MODE_IDS = [
  "text-to-video",
  "image-to-video",
  "video-to-video",
  "music-to-video",
  "motion-sync",
];

/** Map legacy category ids to current generation modes. */
export function normalizeCategoryId(categoryId) {
  const id = String(categoryId || "").trim();
  if (id === "product-in-hand") return "text-to-video";
  if (STUDIO_GENERATION_MODE_IDS.includes(id)) return id;
  return "text-to-video";
}

/**
 * @param {string} [categoryId]
 * @returns {StudioGenerationModeId}
 */
export function getGenerationMode(categoryId) {
  return normalizeCategoryId(categoryId);
}

/**
 * @param {StudioGenerationModeId} mode
 */
export function getGenerationModeRules(mode) {
  switch (mode) {
    case "image-to-video":
      return {
        showPrompt: true,
        showImages: true,
        showVideos: false,
        showMusic: false,
        showVoiceover: true,
        requireImages: true,
        requireVideos: false,
        requireMusic: false,
        allowMultiShots: false,
      };
    case "video-to-video":
      return {
        showPrompt: true,
        showImages: true,
        showReferenceImages: true,
        showVideos: true,
        showMusic: false,
        showVoiceover: true,
        requireImages: false,
        requireVideos: true,
        requireMusic: false,
        allowMultiShots: false,
      };
    case "music-to-video":
      return {
        showPrompt: true,
        showImages: false,
        showVideos: false,
        showMusic: true,
        showVoiceover: false,
        requireImages: false,
        requireVideos: false,
        requireMusic: true,
        allowMultiShots: false,
      };
    case "motion-sync":
      return {
        showPrompt: false,
        showImages: false,
        showVideos: false,
        showMusic: false,
        showVoiceover: false,
        requireImages: true,
        requireVideos: true,
        requireMusic: false,
        allowMultiShots: false,
      };
    default:
      return {
        showPrompt: true,
        showImages: false,
        showVideos: false,
        showMusic: false,
        showVoiceover: true,
        requireImages: false,
        requireVideos: false,
        requireMusic: false,
        allowMultiShots: false,
      };
  }
}

/** @param {object} form */
export function formHasUploadedImage(form) {
  const hasFile = (files) =>
    (files || []).some((f) => f?.file || f?.previewUrl);
  return hasFile(form?.imageUrlUploads) || hasFile(form?.referenceImageUploads);
}

/** Image on a single scene card (Clip / scene plan). */
export function sceneHasUploadedImage(scene) {
  return (scene?.imageUrlUploads || []).some((f) => f?.file || f?.previewUrl);
}

/** Top-level or any scene has a usable image. */
export function formOrScenesHaveImage(form) {
  if (formHasUploadedImage(form)) return true;
  return (form?.scenes || []).some((s) => sceneHasUploadedImage(s));
}

/** @param {object} form */
export function formHasUploadedVideo(form) {
  return (form?.referenceVideoUploads || []).some((f) => f?.file || f?.previewUrl);
}

/** Video on a scene card. */
export function sceneHasUploadedVideo(scene) {
  return (scene?.referenceVideoUploads || []).some((f) => f?.file || f?.previewUrl);
}

/** Top-level or any scene has a reference video. */
export function formOrScenesHaveVideo(form) {
  if (formHasUploadedVideo(form)) return true;
  return (form?.scenes || []).some((s) => sceneHasUploadedVideo(s));
}

/** @param {object} form */
export function formHasUploadedAudio(form) {
  return (form?.referenceAudioUploads || []).some((f) => f?.file || f?.previewUrl);
}

/** Clear media fields that do not apply to the selected generation mode. */
export function mediaFieldsForMode(mode) {
  if (mode === "image-to-video") {
    return {
      referenceVideoUploads: [],
      referenceAudioUploads: [],
      multiShots: false,
      voiceSource: "ai",
      klingVoicePreset: "",
      klingVoiceLabel: "",
    };
  }
  if (mode === "video-to-video") {
    return {
      referenceAudioUploads: [],
      multiShots: false,
      voiceSource: "ai",
      klingVoicePreset: "",
      klingVoiceLabel: "",
    };
  }
  if (mode === "music-to-video") {
    return {
      imageUrlUploads: [],
      referenceImageUploads: [],
      referenceVideoUploads: [],
      multiShots: false,
      voiceSource: "ai",
      klingVoicePreset: "",
      klingVoiceLabel: "",
      musicSource: "upload",
      libraryMusicJobId: null,
      libraryMusicTrackIndex: null,
      libraryMusicTitle: "",
    };
  }
  if (mode === "motion-sync") {
    return {
      referenceImageUploads: [],
      referenceAudioUploads: [],
      multiShots: false,
      voiceSource: "ai",
      klingVoicePreset: "",
      klingVoiceLabel: "",
      musicSource: "upload",
      libraryMusicJobId: null,
      libraryMusicTrackIndex: null,
      libraryMusicTitle: "",
    };
  }
  return {
    imageUrlUploads: [],
    referenceImageUploads: [],
    referenceVideoUploads: [],
    referenceAudioUploads: [],
    multiShots: false,
    voiceSource: "ai",
    klingVoicePreset: "",
    klingVoiceLabel: "",
    musicSource: "upload",
    libraryMusicJobId: null,
    libraryMusicTrackIndex: null,
    libraryMusicTitle: "",
  };
}
